/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        const section = this.closest('.video-section');
        if (section) {
          section.addEventListener('click', this.play.bind(this));
        }
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              autoplay: this.autoplay ? 1 : 0,
              mute: this.autoplay || this.background ? 1 : 0,
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0,
              origin: window.location.origin
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('https://www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay || this.background
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.markPlayed();
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('https://player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');
      if (!this.player) return;

      // Eager load for background/autoplay — loading="lazy" blocks storefront playback
      this.player.removeAttribute('loading');
      this.player.setAttribute('playsinline', '');
      this.player.setAttribute('webkit-playsinline', '');
      this.player.playsInline = true;
      this.player.muted = true;
      this.player.defaultMuted = true;
      this.player.setAttribute('muted', '');
      if (this.background || this.autoplay) {
        this.player.loop = true;
        this.player.preload = 'auto';
      }

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.markPlayed();
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.markPlayed();
      } else {
        this.addObserver(this.player);
        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay || this.background) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.markPlayed();
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Marks the parent section as played so the poster can hide.
     */
    markPlayed() {
      const section = this.closest('.video-section');
      if (section) section.classList.add('video-section--played');
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${Math.max(this.clientHeight, 1) * 1.7778}px`;

        const resizeHandler = () => {
          this.style.width = `${Math.max(this.clientHeight, 1) * 1.7778}px`;
        };

        if (typeof window.debounce === 'function') {
          window.addEventListener('resize', window.debounce(resizeHandler, 200));
        } else {
          window.addEventListener('on:debounced-resize', resizeHandler);
        }
      }

      this.iframe = this.querySelector('iframe');
      if (!this.iframe) return;

      this.iframe.title = this.dataset.description || '';
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        if (typeof this.player.playVideo === 'function') {
          this.player.playVideo();
        }
        return;
      }

      if (this.type === 'vimeo') {
        this.player.play().catch(() => {});
        return;
      }

      // HTML5 video — ensure muted before autoplay (browser policy)
      this.player.muted = true;
      this.player.playsInline = true;

      const attempt = () => {
        const result = this.player.play();
        if (result && typeof result.catch === 'function') {
          result.catch(() => {
            // Retry once media can play (common when deferred-media clones the tag)
            if (this.pausedByUser) return;
            const retry = () => this.play();
            this.player.addEventListener('canplay', retry, { once: true });
            this.player.addEventListener('loadeddata', retry, { once: true });
            try {
              this.player.load();
            } catch (e) { /* ignore */ }
          });
        }
      };

      if (this.player.readyState >= 2) {
        attempt();
      } else {
        this.player.addEventListener('loadeddata', attempt, { once: true });
        this.player.addEventListener('canplay', attempt, { once: true });
        try {
          this.player.load();
        } catch (e) { /* ignore */ }
        // Also try immediately — some browsers play with only metadata
        attempt();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (typeof this.player.getPlayerState === 'function' && this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;
      if (this._hasObserver) return;
      this._hasObserver = true;

      // Options must be the 2nd arg to IntersectionObserver (was incorrectly passed to forEach)
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px', threshold: 0 });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
